import React, { useState } from "react";
import { Box, CircularProgress, Modal, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import StyledButton from "../../../../ui/styledButton";
import StyledDivider from "../../../../ui/styledDivider";
import StyledStatusChip from "../../../../ui/styledStatusChip";
import ConfirmDialog from "../../../../ui/confirmDialog";
import { ReactComponent as Close } from "../../../../assets/icons/close-circle.svg";
import PortalUserForm from "./portalUserForm";
import { useStationPortalUsersOfStation } from "../../../../hooks/queries/useStationUser";
import {
  useCreateStationPortalUser,
  useUpdateStationPortalUser,
  useResetStationPortalUserPassword,
  useDeleteStationPortalUser,
} from "../../../../hooks/mutations/useStationUserMutation";
import { useAuthStore } from "../../../../store";
import { permissions } from "../../../../core/routes/permissions";

const PORTAL_URL = process.env.REACT_APP_STATION_PORTAL_URL || "https://foco.goecm.com.np";

const apiError = (error, fallback) => error?.response?.data?.error || fallback;

const warnIfEmailFailed = (res) => {
  if (res?.emailSent === false) {
    toast.warn("The email could not be sent. Use \"Reset password\" to send new credentials.");
  }
};

const dimText = { color: "primary.DimText", fontSize: "12px", fontWeight: "400" };

// One portal user: details, status and actions
function PortalUserRow({ user, canModify, isBusy, onAction }) {
  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
        <Stack spacing="5px">
          <Typography variant="h6" fontSize={16}>{user.name}</Typography>
          <Typography sx={dimText}>{user.email}</Typography>
          {user.mobile && <Typography sx={dimText}>Ph No : {user.mobile}</Typography>}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <StyledStatusChip $status={user.status}>{user.status}</StyledStatusChip>
          {user.isLocked && <StyledStatusChip $status="OFFLINE">Locked</StyledStatusChip>}
          {user.mustChangePassword && <StyledStatusChip $status="PENDING">Password pending</StyledStatusChip>}
        </Stack>
      </Stack>
      <Typography sx={dimText}>
        Last sign-in: {user.lastLoginAt ? dayjs(user.lastLoginAt).format("DD MMM YYYY, hh:mm A") : "Never"}
        {user.isLocked && " · Locked after repeated failed sign-ins (resetting the password unlocks it)"}
      </Typography>
      {canModify && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <StyledButton variant="secondary" width="90" height="36" fontSize="13" p="6" disabled={isBusy} onClick={() => onAction("edit", user)}>
            Edit
          </StyledButton>
          <StyledButton variant="secondary" width="140" height="36" fontSize="13" p="6" disabled={isBusy} onClick={() => onAction("reset", user)}>
            Reset password
          </StyledButton>
          <StyledButton variant="secondary" width="110" height="36" fontSize="13" p="6" disabled={isBusy} onClick={() => onAction("toggle", user)}>
            {user.isActive ? "Deactivate" : "Activate"}
          </StyledButton>
          <StyledButton variant="secondary" width="90" height="36" fontSize="13" p="6" disabled={isBusy} onClick={() => onAction("delete", user)}>
            Remove
          </StyledButton>
        </Stack>
      )}
    </Stack>
  );
}

// Station reports portal (foco) access for one station: any number of portal users,
// each with their own email and password, all seeing only this station.
export default function PortalAccess({ station }) {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canView = hasPermission(permissions.adminManagement.view);
  const canModify = hasPermission(permissions.adminManagement.modify);

  // form: null (closed) | { user: null } (create) | { user } (edit)
  const [form, setForm] = useState(null);
  // confirm: null | { action: "reset" | "toggle" | "delete", user }
  const [confirm, setConfirm] = useState(null);

  const { data: portalUsers = [], isLoading, isError, error } = useStationPortalUsersOfStation(station._id, canView);

  const closeForm = () => setForm(null);

  const createMutation = useCreateStationPortalUser({
    onSuccess: (res) => {
      toast.success("Portal user created and credentials emailed");
      warnIfEmailFailed(res);
      closeForm();
    },
    onError: (err) => toast.error(apiError(err, "Failed to create portal user")),
  });
  const updateMutation = useUpdateStationPortalUser({
    onSuccess: () => {
      toast.success("Portal user updated");
      closeForm();
    },
    onError: (err) => toast.error(apiError(err, "Failed to update portal user")),
  });
  const resetMutation = useResetStationPortalUserPassword({
    onSuccess: (res) => {
      toast.success("Password reset and new credentials emailed");
      warnIfEmailFailed(res);
    },
    onError: (err) => toast.error(apiError(err, "Failed to reset password")),
  });
  const deleteMutation = useDeleteStationPortalUser({
    onSuccess: () => toast.success("Portal user removed"),
    onError: (err) => toast.error(apiError(err, "Failed to remove portal user")),
  });

  if (!canView) return null;

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isBusy = isSaving || resetMutation.isPending || deleteMutation.isPending;
  const editingUser = form?.user;

  const handleAction = (action, user) => (action === "edit" ? setForm({ user }) : setConfirm({ action, user }));

  const handleFormSubmit = (data) => {
    if (editingUser) {
      updateMutation.mutate({ id: editingUser._id, data });
    } else {
      createMutation.mutate({ ...data, station: station._id });
    }
  };

  const confirmUser = confirm?.user;
  const confirmConfig = confirmUser && {
    reset: {
      title: "Reset portal password",
      subtitle: `A new temporary password will be emailed to ${confirmUser.email}. Any open portal session will be signed out.`,
      buttonText: "Reset",
      run: () => resetMutation.mutate(confirmUser._id),
    },
    toggle: {
      title: confirmUser.isActive ? "Deactivate portal access" : "Activate portal access",
      subtitle: confirmUser.isActive
        ? `${confirmUser.name} will be signed out and will not be able to sign in to the station portal. Other users of this station are not affected.`
        : `${confirmUser.name} will be able to sign in to the station portal again.`,
      buttonText: confirmUser.isActive ? "Deactivate" : "Activate",
      run: () => updateMutation.mutate({ id: confirmUser._id, data: { isActive: !confirmUser.isActive } }),
    },
    delete: {
      title: "Remove portal user",
      subtitle: `${confirmUser.email} will be deleted and can no longer sign in. Other users of this station are not affected.`,
      buttonText: "Remove",
      run: () => deleteMutation.mutate(confirmUser._id),
    },
  }[confirm.action];

  // The first user is usually the owner, so a new station starts from the owner's details
  const formDefaults = editingUser
    ? { name: editingUser.name, email: editingUser.email, mobile: editingUser.mobile || "" }
    : portalUsers.length
      ? { name: "", email: "", mobile: "" }
      : { name: station.owner || "", email: station.owner_email || "", mobile: station.owner_phone || "" };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      );
    }
    if (isError) {
      return (
        <Typography sx={{ ...dimText, py: 2 }}>
          {apiError(error, "Could not load portal access for this station")}
        </Typography>
      );
    }
    if (!portalUsers.length) {
      return (
        <Typography sx={{ ...dimText, py: 1 }}>
          No portal users yet. Add one so the station can view its charging, transaction and finance reports.
        </Typography>
      );
    }

    return (
      <Stack spacing={2} divider={<StyledDivider />}>
        {portalUsers.map((user) => (
          <PortalUserRow key={user._id} user={user} canModify={canModify} isBusy={isBusy} onAction={handleAction} />
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ px: { xs: 1, md: 5 }, py: 3, backgroundColor: "secondary.main", borderRadius: "4px" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ color: "primary.DimText", fontSize: "14px", fontWeight: "400" }}>
          Station Portal Access{portalUsers.length > 0 && ` · ${portalUsers.length} user${portalUsers.length === 1 ? "" : "s"}`}
        </Typography>
        <Typography
          component="a"
          href={PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...dimText, textDecoration: "underline" }}
        >
          {PORTAL_URL.replace(/^https?:\/\//, "")}
        </Typography>
      </Stack>
      <Box sx={{ border: "1px solid rgba(255, 255, 255, 0.20)", borderRadius: "4px", p: 2 }}>
        {renderBody()}
        {canModify && !isLoading && !isError && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <StyledButton variant="primary" width="170" height="40" fontSize="14" p="8" disabled={isBusy} onClick={() => setForm({ user: null })}>
              Add portal user
            </StyledButton>
          </Stack>
        )}
      </Box>

      {confirmConfig && (
        <ConfirmDialog
          open
          title={confirmConfig.title}
          subtitle={confirmConfig.subtitle}
          buttonText={confirmConfig.buttonText}
          onClose={() => setConfirm(null)}
          confirmButtonHandle={confirmConfig.run}
        />
      )}

      <Modal open={!!form} onClose={closeForm}>
        <Box sx={modalStyle}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} my={2}>
            <Typography sx={{ color: "secondary.greytext", fontSize: 18, fontWeight: 700 }}>
              {editingUser ? "Edit Portal User" : "Add Portal User"}
            </Typography>
            <Close onClick={closeForm} style={{ cursor: "pointer" }} />
          </Stack>
          <StyledDivider />
          <PortalUserForm
            key={editingUser?._id || "new"}
            defaultValues={formDefaults}
            isEdit={!!editingUser}
            isSaving={isSaving}
            onSubmit={handleFormSubmit}
            onClose={closeForm}
          />
        </Box>
      </Modal>
    </Box>
  );
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "auto",
  bgcolor: "#27292F",
  boxShadow: 10,
  p: 4,
  color: "#fff",
  outline: "none",
};
